import { fetchUtils } from '#lib/utils/fetchUtils.js'
import { addNotification } from '#lib/stores.js'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'
import { DefaultNotification, NotificationType } from '#lib/types/notification.js'
import type { AccountingMailConfigData } from '#lib/types/accountingMailConfigData.js'
import { LotMailNotificationType } from '#lib/types/enums.js'
import dayjs from 'dayjs'
import { AccountingMailConfigState } from './accountingMailConfigState.svelte'

export async function fetchStateOrdinal(lotId: string): Promise<number> {
	return fetchUtils
		.get(`/api/lot/${lotId}/stateOrdinal`)
		.catch(fetchUtils.catchErrorAndShowNotification())
}

export async function fetchShouldSendLetsGoMail(lotId: string): Promise<boolean> {
	return fetchUtils
		.get(`/api/lot/${lotId}/shouldSendLetsGoMail`)
		.catch(fetchUtils.catchErrorAndShowNotification())
}

async function fetchAccountingMailData(lotId: string): Promise<AccountingMailConfigData> {
	return fetchUtils
		.get(`/api/lot/accountingMailData/${lotId}`)
		.catch(fetchUtils.catchErrorAndShowNotification())
}

export async function saveLetsGoMailSetting(lotId: string): Promise<void> {
	await fetchUtils
		.post(`/api/lot/${lotId}/updateSendLetsGoMail`, {}) // the function just toggles in the backend, the actual value is ignored
		.then(() =>
			addNotification({
				message: get(t)('UI.notification.saved'),
				type: NotificationType.SUCCESS,
				timeout: 4000,
				dismissible: false
			})
		)
		.catch(fetchUtils.catchErrorAndShowNotification())
}

/**
 * Returns false if the duration is invalid (NaN, in the past, or unchanged from saved).
 */
function isValidMailDuration(
	mailType: LotMailNotificationType,
	newDuration: number,
	savedDates: Partial<Record<LotMailNotificationType, number>>
): boolean {
	if (isNaN(newDuration)) return false
	// newDuration is midnight of the selected day; +12h shifts to noon before comparing to now
	if (dayjs(dayjs.unix(newDuration).add(12, 'h').toISOString()).isBefore(dayjs(new Date()))) {
		return false
	}
	// reactivity fires a second change event with a ~9h diff — skip unchanged saves
	return newDuration !== savedDates[mailType]
}

async function saveMailDuration(
	lotId: string,
	mailType: LotMailNotificationType,
	newDuration: number
): Promise<void> {
	const request = {
		lotId,
		mailType,
		// +12h shifts midnight timestamp to noon so the backend receives a mid-day ISO string
		newMailDuration: dayjs.unix(newDuration).add(12, 'h').toISOString()
	}
	await fetchUtils
		.post(`/api/lot/${lotId}/accountingMailData/updateDuration`, request)
		.then(() =>
			addNotification({
				message: get(t)('UI.notification.saved'),
				type: NotificationType.SUCCESS,
				timeout: 4000,
				dismissible: false
			})
		)
		.catch(() =>
			addNotification({
				message: get(t)('UI.notification.error.INVALID_DAY'),
				type: NotificationType.ERROR,
				timeout: 3000,
				dismissible: true
			})
		)
}

/** Returns true on success, false when the request failed (notification already shown). */
async function saveTriggerMailDispatching(
	lotId: string,
	currentlyDisabled: boolean
): Promise<boolean> {
	// endpoint always toggles; compute new state upfront to select the right notification message
	const nowDisabled = !currentlyDisabled
	return fetchUtils
		.get(`/api/lot/accountingMailData/${lotId}/triggerMailDispatching`)
		.then(() => {
			addNotification({
				message: get(t)(
					nowDisabled
						? 'UI.mail.config.success.break.dispatching.message'
						: 'UI.mail.config.success.start.dispatching.message'
				),
				messageHint: get(t)(
					nowDisabled
						? 'UI.mail.config.success.break.dispatching.messageHint'
						: 'UI.mail.config.success.start.dispatching.messageHint'
				),
				type: NotificationType.SUCCESS,
				timeout: 4000,
				dismissible: false
			})
			return true
		})
		.catch((error: unknown) => {
			fetchUtils.catchErrorAndShowNotification()(error)
			return false
		})
}

export async function sendMailRequest(
	lotId: string,
	mailIndex: number,
	mailTitle: string
): Promise<void> {
	await fetchUtils
		.get(`/api/lot/accountingMailData/${lotId}/sendMail/${mailIndex}`)
		.then(() =>
			addNotification({
				message: get(t)('UI.mail.config.success.sendMail.title', { mail: mailTitle }),
				type: NotificationType.SUCCESS,
				timeout: 4000,
				dismissible: false
			})
		)
		.catch(fetchUtils.catchErrorAndShowNotification())
}

async function saveMailingList(
	lotId: string,
	mailingList: string[],
	defaultReceiverList: string[]
): Promise<string[] | null> {
	const request = {
		mailingList: mailingList.filter((entry) => !defaultReceiverList.includes(entry))
	}
	return fetchUtils
		.post(`/api/lot/accountingMailData/${lotId}/updateMailingList`, request)
		.catch((error: unknown) => {
			addNotification(
				DefaultNotification(
					(error as { message?: string })?.message ?? String(error),
					NotificationType.ERROR,
					true,
					0
				)
			)
			return null
		})
}

// last backend-confirmed mailing list (incl. default receivers) — restore point for failed saves
let lastSavedMailingList: string[] | null = null

export async function fetchAndApplyAccountingMailData(
	lotId: string,
	mailState: AccountingMailConfigState
): Promise<void> {
	const data = await fetchAccountingMailData(lotId)
	if (!data) return
	mailState.fromData(data)
	lastSavedMailingList = [...mailState.mailingList!]
}

export async function applyMailDurationChange(
	lotId: string,
	mailType: LotMailNotificationType,
	newDuration: number | undefined,
	mailState: AccountingMailConfigState
): Promise<void> {
	if (newDuration === undefined) return
	if (!isValidMailDuration(mailType, newDuration, mailState.savedDates)) return
	await saveMailDuration(lotId, mailType, newDuration)
	await fetchAndApplyAccountingMailData(lotId, mailState)
}

export async function triggerMailDispatching(
	lotId: string,
	mailState: AccountingMailConfigState
): Promise<void> {
	const saved = await saveTriggerMailDispatching(lotId, mailState.isMailDispatchingDisabled)
	if (!saved) return
	mailState.isMailDispatchingDisabled = !mailState.isMailDispatchingDisabled
}

export async function applyMailingListUpdate(
	lotId: string,
	mailState: AccountingMailConfigState
): Promise<void> {
	if (!mailState.mailingList || !mailState.defaultReceiverList) return
	const data = await saveMailingList(lotId, mailState.mailingList, mailState.defaultReceiverList)
	if (!data) {
		// save failed — restore the last backend-confirmed list; syncing mailingListSize keeps
		// length === size so the page $effect does not re-fire another POST
		if (lastSavedMailingList) mailState.mailingList = [...lastSavedMailingList]
		mailState.mailingListSize = mailState.mailingList.length
		return
	}
	// backend returns only the custom entries; prepend default receivers to reflect full list
	mailState.mailingList = data
	mailState.defaultReceiverList.forEach((receiver) => mailState.mailingList!.unshift(receiver))
	mailState.mailingListSize = mailState.mailingList!.length
	lastSavedMailingList = [...mailState.mailingList]
}
